import { FaLinkedin, FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { LEGAL_DOCS } from './legalDocs';

const Footer = ({ loggedInUser, setLoggedInUser }) => {
  const location = useLocation(); // Hook to track current page

  useEffect(() => {
    const currentPath = location.pathname;

    // LIST OF PAGES TO IGNORE
    // We don't want to redirect users back to the login, signup, or password reset pages
    // Legal pages open in their own tab; localStorage is shared across tabs,
    // so saving them would clobber the page the user is actually on.
    const ignoredPaths = ['/login', '/signup', '/forgot-password', '/reset-password', '/payment', ...LEGAL_DOCS.map((doc) => doc.path)];

    // If the current page is NOT in the ignore list, save it as the "return destination"
    // We also check to make sure we aren't saving the 'jump' code urls
    if (!ignoredPaths.includes(currentPath)) {
      localStorage.setItem('postLoginRedirect', currentPath);
    }
  }, [location]);

  // --- LOGIC 2: SILENT VISUAL SYNC (Moved from AccountSettings) ---
  //CURRENTLY THIS ONLY REALLY SETS UP AT THE START WHEN YOU LOG IN AND UNSETS WHEN YOU LOG OUT
  //THIS MEANS IT WILL NOT DYNAMICALLY CHANGE WHEN THE USER FINISHES BUYING SOMETHING
  useEffect(() => {
    const syncUserStatus = async () => {
      // SAFETY VALVE: If no user is logged in, STOP immediately.
      // This prevents it from running after logout.
      if (!loggedInUser || !localStorage.getItem('token')) return;

      try {
        const response = await fetch('https://vicktoria.ai/api/visual-info/status', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const statusData = await response.json();

          // Only update if something actually changed to avoid infinite loops
          if (
            loggedInUser.freeTrialAvailable !== statusData.freeTrialAvailable ||
            loggedInUser.onPaidPlan !== statusData.onPaidPlan ||
            loggedInUser.isSpecialPermission !== statusData.isSpecialPermission
          ) {
            console.log("🔄 Footer: Syncing latest user status...");

            const updatedUser = {
              ...loggedInUser,
              freeTrialAvailable: statusData.freeTrialAvailable,
              onPaidPlan: statusData.onPaidPlan,
              isSpecialPermission: statusData.isSpecialPermission
            };

            setLoggedInUser(updatedUser);
            localStorage.setItem("loggedInUser", JSON.stringify(updatedUser));
          }
        }
      } catch (err) {
        // Silent fail is fine here; we just use the existing data
      }
    };

    syncUserStatus();
    // Run this whenever the user object changes (e.g. they log in) or the page changes
  }, [location.pathname, loggedInUser?.email]);

  const footerStyle = {
    backgroundColor: '#B3CFDB',
    width: '100%',
    padding: '20px 0', // Adds breathing room
    marginTop: 'auto', // Pushes to bottom if using a flex container
    opacity: 0.7,
    color: '#2B4B6F',
    fontSize: '12px',
    fontFamily: "'Inter', sans-serif",
    textAlign: 'center',
    zIndex: 1050,
    position: 'relative', // Changed from absolute/fixed logic
    pointerEvents: 'auto',
    boxSizing: 'border-box'
  };

  return (
    <footer style={footerStyle}>

      <div
        style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <a href="https://www.linkedin.com/company/vicktoriaai/" target="_blank" rel="noreferrer" aria-label="Vicktoria AI on LinkedIn" style={{ color: 'inherit', cursor: 'pointer' }}>
          <FaLinkedin size={18} />
        </a>

        <a href="https://x.com/VicktoriaAI" target="_blank" rel="noreferrer" aria-label="Vicktoria AI on X" style={{ color: 'inherit', cursor: 'pointer' }}>
          <FaXTwitter size={18} />
        </a>

        <a href="https://www.facebook.com/vicktoriaai" target="_blank" rel="noreferrer" aria-label="Vicktoria AI on Facebook" style={{ color: 'inherit', cursor: 'pointer' }}>
          <FaFacebookF size={18} />
        </a>

        <a href="https://www.instagram.com/vicktoriaai/" target="_blank" rel="noreferrer" aria-label="Vicktoria AI on Instagram" style={{ color: 'inherit', cursor: 'pointer' }}>
          <FaInstagram size={18} />
        </a>

        <a href="https://www.youtube.com/@VicktoriaAI" target="_blank" rel="noreferrer" aria-label="Vicktoria AI on YouTube" style={{ color: 'inherit', cursor: 'pointer' }}>
          <FaYoutube size={18} />
        </a>
      </div>

      {/* Legal documents — each opens in its own tab (the page has a
          "Return to previous page" control that closes it again). */}
      <nav
        aria-label="Legal"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 10px',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '10px',
          padding: '0 16px',
        }}
      >
        {LEGAL_DOCS.map((doc, i) => (
          <React.Fragment key={doc.path}>
            {i > 0 && <span aria-hidden="true">·</span>}
            <a
              href={doc.path}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {doc.linkLabel}
            </a>
          </React.Fragment>
        ))}
      </nav>

      © 2026 Vicktoria AI Corp. All Rights Reserved.

    </footer>
  );
};

export default Footer;